import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { analyticsOptibotStarted } from '@/lib/analytics-service';
import type { ConnState, DeviceSessionOutput } from '../hooks/useDeviceSessionStream';

/** A device-troubleshoot session focused into the roomy panel surface (not the cramped chat card).
 *  'live' opens the SSE + shows the steer composer; 'history' replays the recorded transcript (read-only). */
export interface FocusedSession {
  session: DeviceSessionOutput;
  mode: 'live' | 'history';
  /** Human label for the header (e.g. the screen name) when we have it. */
  deviceLabel?: string;
}

/** OptiBot panel display modes. */
export type OptibotViewMode = 'sidebar' | 'floating';

/** Resizable-sidebar bounds. Max is a fraction of the viewport width so the
 *  panel can never take over the whole page; on small viewports the max sits
 *  close to the min, so there is little/no room to drag wider. */
export const SIDEBAR_MIN_WIDTH = 360;
export const SIDEBAR_DEFAULT_WIDTH = 420;
export const SIDEBAR_MAX_VW = 0.45;

/** Floating-card geometry (px). The card is anchored bottom-right. These feed
 *  BOTH the panel's own height AND the Get Started checklist's raised offset —
 *  when both are open the checklist stacks directly above the floating card, so
 *  the card shrinks (`*_WITH_CHECKLIST`) to leave vertical room for the checklist.
 *  Keep the two consumers (OptiBotPanel + GetStartedChecklist) in sync via these. */
export const FLOATING_BOTTOM_PX = 16; // bottom-4
export const FLOATING_HEIGHT_PX = 520; // default tall card (no checklist)
export const FLOATING_HEIGHT_WITH_CHECKLIST_PX = 420; // shorter card when checklist is stacked above
export const FLOATING_CHECKLIST_GAP_PX = 16; // gap between the card's top and the checklist's bottom

interface OptibotState {
  isOpen: boolean;
  isExpanded: boolean;
  viewMode: OptibotViewMode;
  /** Persisted sidebar width in px (clamped to [min, max-vw] when applied). */
  sidebarWidth: number;
  showHistory: boolean;
  /** When true, the top contact-support callout shows its compact one-line form
   *  (headset + label only); when false it shows the full two-line details. */
  supportCollapsed: boolean;
  /** Runtime-only: window is too narrow to dock the sidebar, so the panel renders
   *  as a floating overlay instead (the user's chosen viewMode is preserved). */
  isNarrow: boolean;
  /** Runtime-only: the Get Started checklist is showing its bottom-right panel.
   *  While true the OptiBot panel is forced to floating (the docked sidebar would
   *  collide with the fixed bottom-right checklist) and shrinks to leave room for
   *  the checklist stacked above it. Published by GetStartedChecklist; the user's
   *  chosen viewMode is preserved and restored when the checklist hides. */
  checklistOpen: boolean;
  /** When set, the panel shows this session full-size instead of the chat (the chat thread is preserved). */
  focusedSession: FocusedSession | null;
  /** Last-known connection state per session (reported by the live view) → drives the chat card's status badge. */
  sessionConn: Record<string, ConnState>;
  /** Sessions already auto-opened once (so re-renders / "back to chat" don't reopen). */
  autoOpened: Record<string, boolean>;
  /** Set when the user APPROVES an openDeviceTroubleshootSession call → the resulting session card
   *  auto-opens once. The approval (never replayed on reload) is what makes a start "fresh". */
  sessionStartApproved: boolean;
  setOpen: (open: boolean) => void;
  setExpanded: (expanded: boolean) => void;
  setViewMode: (mode: OptibotViewMode) => void;
  setSidebarWidth: (width: number) => void;
  setShowHistory: (show: boolean) => void;
  setSupportCollapsed: (collapsed: boolean) => void;
  setIsNarrow: (isNarrow: boolean) => void;
  setChecklistOpen: (checklistOpen: boolean) => void;
  toggle: () => void;
  /** Open a session into the roomy surface (auto-expands the panel so the transcript has room). */
  openSession: (focused: FocusedSession) => void;
  closeSession: () => void;
  /** The live view reports its connection state here so the chat card can show a status badge. */
  setSessionConn: (sessionId: string, conn: ConnState) => void;
  /** Mark a session auto-opened (idempotent guard for the open-on-start behavior). */
  markAutoOpened: (sessionId: string) => void;
  /** Flag/clear that the user just approved starting a session (drives the one-shot auto-open). */
  setSessionStartApproved: (approved: boolean) => void;
}

export const useOptibotStore = create<OptibotState>()(
  persist(
    (set) => ({
      isOpen: false,
      isExpanded: false,
      viewMode: 'sidebar',
      sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
      showHistory: false,
      supportCollapsed: false,
      isNarrow: false,
      checklistOpen: false,
      focusedSession: null,
      sessionConn: {},
      autoOpened: {},
      sessionStartApproved: false,
      setOpen: (isOpen) => {
        if (isOpen) {
          analyticsOptibotStarted({ sourcePage: window.location.pathname });
        }
        set({ isOpen });
      },
      setExpanded: (isExpanded) => set({ isExpanded }),
      setViewMode: (viewMode) => set({ viewMode }),
      setSidebarWidth: (sidebarWidth) => set({ sidebarWidth }),
      setShowHistory: (showHistory) => set({ showHistory }),
      setSupportCollapsed: (supportCollapsed) => set({ supportCollapsed }),
      setIsNarrow: (isNarrow) => set({ isNarrow }),
      setChecklistOpen: (checklistOpen) => set({ checklistOpen }),
      toggle: () =>
        set((state) => {
          if (!state.isOpen) {
            analyticsOptibotStarted({ sourcePage: window.location.pathname });
          }
          return { isOpen: !state.isOpen };
        }),
      // Open the session in place (panel stays whatever width the user set — no auto-expand).
      openSession: (focusedSession) => set({ focusedSession, isOpen: true }),
      closeSession: () => set({ focusedSession: null }),
      setSessionConn: (sessionId, conn) =>
        set((s) =>
          s.sessionConn[sessionId] === conn
            ? s
            : { sessionConn: { ...s.sessionConn, [sessionId]: conn } },
        ),
      markAutoOpened: (sessionId) =>
        set((s) =>
          s.autoOpened[sessionId] ? s : { autoOpened: { ...s.autoOpened, [sessionId]: true } },
        ),
      setSessionStartApproved: (sessionStartApproved) => set({ sessionStartApproved }),
    }),
    {
      name: 'optibot-view',
      // Persist only the view preferences — never the open state or transient session state.
      partialize: (s) => ({
        viewMode: s.viewMode,
        sidebarWidth: s.sidebarWidth,
        supportCollapsed: s.supportCollapsed,
      }),
      // Coerce any retired/invalid persisted viewMode (e.g. the removed
      // 'fullscreen') back to the default so the panel never reads a bad value.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<OptibotState>;
        const viewMode: OptibotViewMode = p.viewMode === 'floating' ? 'floating' : 'sidebar';
        return { ...current, ...p, viewMode };
      },
    },
  ),
);
